import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { t } from 'ttag';
import { Dialog, Transition } from '@headlessui/react';
import { RiCloseFill } from 'react-icons/ri';

import { Button, Text } from '@components';

import { formatPrizeDay, getPrizeState } from '../utils';
import PrizeStatus from './PrizeStatus';
import prizePropType from './prizePropType';

/**
 * A prize's full description. A bottom sheet on mobile, a dialog from `md`.
 */
export default function PrizeDetails({
  isOpen,
  prize,
  termsLink,
  getButtonText,
  onClose,
  onParticipate
}) {
  const attributes = prize?.attributes;

  // The Kindly chat bubble sits above every dialog and would cover the sheet's actions.
  useEffect(() => {
    const kindlyChat = window?.kindlyChat;
    if (!isOpen || !kindlyChat?.hideBubble) return undefined;

    kindlyChat.hideBubble();
    return () => kindlyChat.showBubble?.();
  }, [isOpen]);

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog className="relative z-[150]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
        </Transition.Child>
        <div className="fixed inset-0 flex items-end md:items-start md:justify-center md:overflow-y-auto md:p-4 md:pt-[120px]">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="translate-y-full md:translate-y-4 md:opacity-0"
            enterTo="translate-y-0 md:opacity-100"
            leave="ease-in duration-200"
            leaveFrom="translate-y-0 md:opacity-100"
            leaveTo="translate-y-full md:translate-y-4 md:opacity-0"
          >
            {/* dvh follows the visible area as mobile browser toolbars show and hide; the
                bottom padding clears the home indicator if the page uses viewport-fit=cover. */}
            <Dialog.Panel className="relative grid w-full gap-3.5 overflow-y-auto rounded-t-[22px] bg-white px-5 pb-[calc(1.5rem_+_env(safe-area-inset-bottom))] pt-2.5 motion-reduce:transition-none max-md:max-h-[88vh] max-md:supports-[height:100dvh]:max-h-[88dvh] md:w-[600px] md:max-w-full md:rounded-[18px] md:px-8 md:pb-8 md:pt-7">
              <div
                className="h-1 w-10 justify-self-center rounded-full bg-gray md:hidden"
                aria-hidden="true"
              />
              <button
                type="button"
                aria-label={t`Close Modal`}
                onClick={onClose}
                className="absolute right-3 top-3 z-10 m-0 grid h-8 w-8 cursor-pointer place-items-center rounded-full border-0 bg-white-smoke p-0 text-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-black"
              >
                <RiCloseFill className="h-4 w-4" />
              </button>
              {attributes && (
                <>
                  {attributes.image && (
                    <img
                      src={attributes.image}
                      alt=""
                      // Tailwind's aspect-ratio utilities are disabled in this project.
                      style={{ aspectRatio: '16 / 10' }}
                      className="w-full rounded-[14px] object-cover object-top"
                    />
                  )}
                  <div className="flex items-center gap-2">
                    <PrizeStatus
                      attributes={attributes}
                      className="bg-whisper"
                    />
                    <span className="text-xs font-semibold uppercase tracking-wide text-dark-gray">
                      {formatPrizeDay(attributes.valid_from)}
                    </span>
                  </div>
                  <Dialog.Title
                    as="h3"
                    className="m-0 text-[22px] font-extrabold uppercase leading-[1.1] text-black"
                  >
                    {attributes.title}
                  </Dialog.Title>
                  <Text className="whitespace-pre-line text-[15px] leading-relaxed text-night-rider">
                    {attributes.description}
                  </Text>
                  <Button
                    size="md"
                    className="w-full justify-center"
                    onClick={() => onParticipate(prize.id)}
                    disabled={getPrizeState(attributes) !== 'today'}
                  >
                    <Button.Text>{getButtonText(attributes)}</Button.Text>
                  </Button>
                  <a
                    href={termsLink}
                    target="_blank"
                    rel="noreferrer"
                    className="justify-self-start text-sm text-black underline"
                  >{t`Read Unisport's Competition Terms`}</a>
                </>
              )}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

PrizeDetails.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  prize: prizePropType,
  termsLink: PropTypes.string,
  getButtonText: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onParticipate: PropTypes.func.isRequired
};

PrizeDetails.defaultProps = {
  prize: null,
  termsLink: undefined
};
